/* ImageDetailsComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ImageDetailsRenderer");

class ImageDetailsComponent extends EventEmitter {
    constructor(logger, gameEditor, state, io, OsImageCatalogEntity, OsImageEntity, OsUpgradePlanPrefs, userSession, OsImageServerPrefsEntity) {
        super();

        this._io = io;
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._activeOrgId = this._gameEditor.activeOrgId;
        this._modalUserFeedback = this._gameEditor._modalUserFeedback;
        this._state = state;
        this._executionEntity = this._state.configExecutionEntity;
        this._osPlan = this.gameEditor.getPlan();
        this._osImageCatalogEntity = OsImageCatalogEntity;
        this._filteredCatalogs = undefined;
        this._isDirty = false;
        this._detectedSku = this._getDetectedSku();
        this._osImageEntity = OsImageEntity;
        this._osUpgradePrefs = OsUpgradePlanPrefs;
        this._osImageServerPrefsEntity = OsImageServerPrefsEntity;
        this._osUpgradePrefEntity = undefined;
        this._authorizedActions = userSession.user.authorizedActions;
        this._key = this._executionEntity._type === "Device" ? this._executionEntity._id : this._detectedSku;
        this._images = [];
        this._imagesFeed = [];
        this._patchesCreated = [];
        this._imageSortOrder = [];
        this._userId = _.get(this._gameEditor._userSession, "user.id", "");

        this._renderer = new Renderer(this);

        this._registerListeners();
    }
    whenModelIsReady() {
        return this._osImageCatalogEntity
            .loadOsImageCatalogs(
                this._activeOrgId,
                {},
                {},
                { fileName: 1 },
                {
                    skip: 0,
                    limit: 40
                }
            )
            .then((catalogs) => {
                this._startMatchingCatalogsImagesFeed(catalogs);
                return Promise.all(
                    this._imagesFeed.map((imageFeed) => {
                        return imageFeed.whenInitialized();
                    })
                );
            })
            .then((imagesFeed) => {
                imagesFeed.map((feed) => {
                    Object.values(feed.cache).map((image) => {
                        if (!this._images.some((currentImage) => currentImage.id === image.id)) {
                            this._images.push(image);
                        }
                    });
                });
                return this.getOsUpgradePrefs();
            })
            .then((osUpgradePref) => {
                this._osUpgradePrefEntity = osUpgradePref;
                Promise.resolve();
            });
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {
        this._osPlan.on("DirtyStateChanged", (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this)));
    }
    _unregisterListeners() {
        this._osPlan.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this.getGameEditor();
    }

    get logger() {
        return this._logger;
    }

    getGameEditor() {
        return this._gameEditor;
    }

    get key() {
        return this._key;
    }

    get isDirty() {
        return this._isDirty;
    }

    set isDirty(newValue) {
        this._isDirty = newValue;
        return true;
    }

    getOsUpgradePrefs() {
        return this._osUpgradePrefs.loadPrefs(this._activeOrgId, {
            catalogCreationAuthGroup: true,
            catalogUsage: true,
            enableCatalogs: true
        });
    }

    getAssociationProtocols() {
        let associationProtocols = [];
        const protocolValues = _.get(this._osImageServerPrefsEntity, "pristineProperties.protocols");
        for (let key in this.gameEditor.planEntity.ASSOCIATION_PROTOCOLS) {
            if (protocolValues[key.toLowerCase()] === true || protocolValues[key.toLowerCase()] === undefined) {
                let association = {
                    label: key,
                    value: key
                };
                associationProtocols.push(association);
            } else {
                let association = {
                    label: key,
                    value: key,
                    disabled: true
                };
                associationProtocols.push(association);
            }
        }
        return associationProtocols;
    }

    getSelectedAssociation() {
        if (
            _.get(this._osPlan.getPristineProperties(), "imageAssociations", false) &&
            _.get(this._osPlan.getPristineProperties().imageAssociations, this._executionEntity._id, false)
        ) {
            return _.get(this._osPlan.getPristineProperties().imageAssociations, this._executionEntity._id, {}).protocol;
        } else if (
            _.get(this._osPlan.getPristineProperties(), "imageAssociations", false) &&
            _.get(this._osPlan.getPristineProperties().imageAssociations, this._key, false)
        ) {
            return _.get(this._osPlan.getPristineProperties().imageAssociations, this._key, {}).protocol;
        }
    }

    getSelectedWindowSize() {
        if (
            _.get(this._osPlan.getPristineProperties(), "imageAssociations", false) &&
            _.get(this._osPlan.getPristineProperties().imageAssociations, this._executionEntity._id, false)
        ) {
            return _.get(this._osPlan.getPristineProperties().imageAssociations, this._executionEntity._id, {}).copyBlockSize;
        } else if (
            _.get(this._osPlan.getPristineProperties(), "imageAssociations", false) &&
            _.get(this._osPlan.getPristineProperties().imageAssociations, this._key, false)
        ) {
            return _.get(this._osPlan.getPristineProperties().imageAssociations, this._key, {}).copyBlockSize;
        }
    }

    shutdown() {
        return this._backToDetailsClick()
            .then(() => {
                return this._shutdown();
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "ImageDetailsComponent._saveImageAssociations() error.");
                this._modalUserFeedback.displayError(err);
            });
    }

    _shutdown() {
        this._unregisterListeners();
        return Promise.all([
            this._imagesFeed.map((imageFeed) => {
                imageFeed.shutdown();
            })
        ])
            .then(() => {
                return this._renderer._shutdown();
            })
            .then(() => {
                this._renderer = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._imagesFeed = undefined;
                this._images = undefined;
                this._key = undefined;
                this._detectedSku = undefined;
                this._patchesCreated = undefined;
                this._osUpgradePrefs = undefined;
                this._osUpgradePrefEntity = undefined;
                this._osImageEntity = undefined;
                this._imageSortOrder = undefined;
                this._authorizedActions = undefined;
                Promise.resolve();
            });
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    _startMatchingCatalogsImagesFeed(catalogs) {
        if (this._detectedSku) {
            catalogs.filter((catalog) => {
                return _.get(catalog.getPristineProperties(), "skus", []).some((sku) => {
                    let strRegExp = sku.skusCol;
                    if (strRegExp) {
                        if (strRegExp[0] !== "/") {
                            strRegExp = "/" + strRegExp;
                            if (!strRegExp.match(/^\/.+\/[gimy]*$/)) {
                                strRegExp = strRegExp + "/";
                            }
                        }

                        let parts = strRegExp.match(/^\/(.+)\/([gimy]*)$/);

                        if (parts) {
                            let objRegExp = new RegExp(parts[1], parts[2]);
                            if (this._detectedSku.match(objRegExp)) {
                                this._imagesFeed.push(
                                    catalog.startRelatedOsImagesFeed(
                                        {},
                                        {},
                                        { fileName: 1 },
                                        {
                                            skip: 0,
                                            limit: 200
                                        }
                                    )
                                );
                            }
                        }
                    } else {
                        this._logger.warn({}, "ImageDetailsComponent._startMatchingCatalogsImagesFeed() error: Invalid regular expression");
                    }
                });
            });
        }
        return [];
    }

    isDirtyChecked() {
        if (this._gameEditor._tabCmpnt.isDirty()) {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive"
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text"
                    }
                })
                .then(() => {
                    return this._saveImageAssociations().then(() => Promise.resolve()); // it will not close the tab if it will have something in result
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve()
                            .then(() => {
                                this._osPlan.clearPendingRelationships();
                                this._osPlan.clearPendingPatches();
                            })
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => Promise.resolve());
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        } else {
            return Promise.resolve();
        }
    }

    getUserInfo() {
        return this._gameEditor._userService.get({ userId: this._userId });
    }

    isUserAuthorizedForCreate() {
        if (this._authorizedActions.indexOf("osmCatalogWrite") >= 0) {
            return Promise.resolve(true);
        }
        return Promise.resolve(false);
    }

    _backToDetailsClick() {
        if (!this._osPlan.isDirty()) {
            return Promise.resolve();
        }

        if (this._isWindowSizeInvalid()) {
            return Promise.reject({ errMessage: "Window Size value is invalid." });
        }

        return Promise.resolve()
            .then(() => {
                return this._modalUserFeedback.displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive"
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text"
                    }
                });
            })
            .then(() => {
                this._saveImageAssociations(); // it will not close the tab if it will have something in result
                return Promise.resolve();
            })
            .catch((err) => {
                if (err.userRejected) {
                    return Promise.resolve()
                        .then(() => {
                            this._osPlan.clearPendingRelationships();
                            this._osPlan.clearPendingPatches();
                        })
                        .then(() => this._modalUserFeedback.hideDisplayed())
                        .then(() => Promise.resolve());
                } else {
                    console.error("Error in onBeforeUnloadFunc! Err:", err);
                }
            });
    }

    _isWindowSizeInvalid() {
        return this.renderer._windowSize.isInvalid();
    }

    _saveImageAssociations() {
        if (!this._isWindowSizeInvalid()) {
            this._osPlan.setStatus("DRAFT");
            return this._osPlan
                .update()
                .then(() => {
                    if (!_.isEmpty(this.renderer)) {
                        this.renderer._toggleAction(this._osPlan.isDirty());
                    }
                    this._patchesCreated = [];
                })
                .catch((err) => {
                    this._logger.warn({ error: err }, "ImageDetailsComponent._saveImageAssociations() error.");
                    this._modalUserFeedback.displayError(err);
                    throw err;
                });
        } else {
            return Promise.reject();
        }
    }

    _getDetectedSku() {
        return _.get(this._executionEntity, "detectedSku", _.get(this._executionEntity, "groupingValue"));
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = ImageDetailsComponent;
ImageDetailsComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.io",
    "$teve.entities.OsImageCatalog",
    "$teve.entities.OsImage",
    "$teve.entities.OsUpgradePlanPrefs",
    "$teve.userSession"
];
ImageDetailsComponent.$displayDetails = {
    type: "View",
    name: "Select Images",
    icon: "common.edit",
    shown: false
};
